import React from 'react';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { IconInfo, IconKey, IconMobile } from '@wds/icons';
import { Popout } from '@wds/popout';
import { Switch } from '@wds/switch';
import { Tooltip } from '@wds/tooltip';
import { Typography } from '@wds/typography';

import PropTypes from 'prop-types';

import STRINGS from '../../../constants';
import CardTemplate from '../../../../sma.common/components/react/components/CardTemplate';
import ImageUpload from '../../../../sma.common/components/react/components/imageUpload/imageUpload';
import EditableTextField from '../../../../sma.common/components/react/components/EditableTextField';
import EditableSelectField from '../../../../sma.common/components/react/components/EditableSelectField';

const tabStrings = STRINGS.accountSettingsPage.myProfileTab;

const MyProfileTab = (props) => {
	const {
		settings,
		setSettings,
		languages,
		timezones,
		cancelEmailChangeRequest,
		resendEmailChangeRequest,
		handleDisconnectGoogleSignOn,
		reset,
		editingFields,
		setEditingFields,
		assumedRole,
		usesSso,
		usesGoogleSSO,
	} = props;
	return (
		<>
			<CardTemplate
				cardTitle={tabStrings.profileImage}
				id='profile-image'
			>
				<Box id='profile-image'>
					<Box px={3}>
						<ImageUpload
							label={tabStrings.profileImage}
							id='profile-image'
							acceptTypes='image/png;base64'
							url='/internal/user/image/'
						/>
					</Box>
				</Box>
			</CardTemplate>
			<CardTemplate cardTitle={tabStrings.personalInfo} id='personal-info'>
				<Grid noBleeds>
					<Grid.Item>
						<Grid noBleeds>
							<Grid.Item>
								{Object.keys(settings).length > 0 && (
									<EditableTextField
										id='name'
										label={STRINGS.name}
										fields={[
											{
												id: 'first_name',
												label: STRINGS.firstName,
												type: 'text',
												isRequired: true,
												errorMessage: STRINGS.fieldErrors.text,
												value: settings?.first_name,
											},
											{
												id: 'last_name',
												label: STRINGS.lastName,
												type: 'text',
												isRequired: true,
												errorMessage: STRINGS.fieldErrors.text,
												value: settings?.last_name,
											},
										]}
										editable={(!usesSso && !usesGoogleSSO)}
										state={settings}
										setState={setSettings}
										reset={reset}
										editingFields={editingFields}
										setEditingFields={setEditingFields}
									/>
								)}
							</Grid.Item>
							<Grid.Item>
								{Object.keys(settings).length > 0 && (
									<>
										<EditableTextField
											id='email'
											label={STRINGS.email}
											fields={[
												{
													id: 'email',
													label: STRINGS.email,
													type: 'email',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.email,
													value: settings?.email,
												},
											]}
											editable={(!usesSso && !usesGoogleSSO && !settings?.temp_email)}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
										{settings.temp_email && (
											<Box mt={1} style={{ maxWidth: 350 }}>
												<Popout
													size='lg'
													trigger={(
														<Box display='flex' gap={2}>
															<IconInfo
																size='sm'
																title='info'
																color='darkMuted'
															/>
															<Typography
																variant='caption'
																weight='medium'
																lineHeight='1'
															>
																{STRINGS.emailAddressChangePending}
															</Typography>
														</Box>
													)}
												>
													<Popout.Body>
														{tabStrings.emailChangeRequested}
														{' '}
														{settings.temp_email}
														{'. '}
														{tabStrings.emailChangeCheckEmail}
													</Popout.Body>
													<Popout.Footer align='left'>
														<Button
															size='sm'
															color='warning'
															onClick={() => cancelEmailChangeRequest()}
														>
															{STRINGS.cancel}
														</Button>
														<Button
															size='sm'
															color='alt'
															onClick={() => resendEmailChangeRequest()}
														>
															{STRINGS.resendRequest}
														</Button>
													</Popout.Footer>
												</Popout>
											</Box>
										)}
									</>
								)}
							</Grid.Item>
						</Grid>
					</Grid.Item>
				</Grid>
				{!assumedRole && (
					<Grid noBleeds>
						{!usesGoogleSSO && (
							<Grid.Item xs={12}>
								<Grid mt={4} noBleeds>
									<Grid.Item>
										{Object.keys(settings).length > 0 && (
											<EditableTextField
												id='password'
												label={STRINGS.password}
												fields={[
													{
														id: 'current_password',
														label: STRINGS.currentPassword,
														type: 'password',
														errorMessage: STRINGS.fieldErrors.currentPassword,
														value: settings?.current_password || '',
														isRequired: true,
													},
													{
														id: 'new_password',
														label: STRINGS.newPassword,
														type: 'password',
														errorMessage: STRINGS.fieldErrors.newPassword,
														value: settings?.new_password || '',
													},
												]}
												state={settings}
												setState={setSettings}
												reset={reset}
												editingFields={editingFields}
												setEditingFields={setEditingFields}
											/>
										)}
									</Grid.Item>
								</Grid>
							</Grid.Item>
						)}
						{!usesSso && !usesGoogleSSO && (
							<>
								<Grid.Item xs={12}>
									<Grid noBleeds>
										<Grid.Item>
											<Box display='flex' justifyContent='space-between'>
												<Box display='flex' gap={2}>
													<Typography
														color='muted'
														weight='medium'
														id='mfa-switch-label'
													>
														{tabStrings.mfaEnabled}
													</Typography>
												</Box>
												<Tooltip text='Coming soon' placement='right'>
													<Switch
														checked={settings.mfa_enabled}
														id='mfa-enabled'
														disabled
														aria-labelledby='mfa-switch-label'
														aria-label={STRINGS.labels.mfaSwitch}
													/>
												</Tooltip>
											</Box>
										</Grid.Item>
									</Grid>
								</Grid.Item>
								<Grid.Item>
									{/* <Box display='flex' gap={2}>
										<Typography color='muted'>
											{tabStrings.mfaLearnMoreText}
										</Typography>
										<Typography
											color='muted'
											component='a'
											href='#'
										>
											{tabStrings.mfaLearnMoreLink}
										</Typography>
									</Box> */}
								</Grid.Item>
							</>
						)}
					</Grid>
				)}
				{
					settings?.mfa_enabled && (
						<Grid noBleeds mt={4}>
							<Grid.Item>
								<Box
									display='flex'
									gap={4}
									justifyContent='flex-start'
									flexWrap
								>
									<Button
										color='alt'
										id='change-device'
										// onClick={handleChangeDevice}
										disabled
									>

										<IconMobile size='sm' title='mobile_icon' />
										{tabStrings.changeEnrolledDevice}
									</Button>
									<Button
										color='alt'
										id='generate-codes'
										// onClick={handleGenerateCodes}
										disabled
									>
										<IconKey size='sm' title='key_icon' />
										{tabStrings.generateNewCodes}
									</Button>
								</Box>
							</Grid.Item>
						</Grid>
					)
				}
			</CardTemplate>
			{(!assumedRole || assumedRole.super) && settings?.social && Object.keys(settings?.social).length > 0 && (
				<CardTemplate
					cardTitle={tabStrings.linkedAccounts}
					id='linked-accounts'
				>
					<Box id='linked-accounts-id'>
						<Grid noBleeds xs={16}>
							<Grid.Item xs={12}>
								<Box>
									<Box>
										<Typography
											variant='bodySm'
											weight='medium'
										>
											{tabStrings.socialSignOn}
										</Typography>
									</Box>
									<Box display='flex' justifyContent='space-between'>
										<Typography
											lineHeight={2.5}
											color='link'
										>
											{STRINGS.googleSSO}
											{' '}
											(
											{settings.social.id}
											)
										</Typography>
										<Button
											color='warning'
											onClick={() => handleDisconnectGoogleSignOn()}
											id='disable-google'
										>
											{tabStrings.disconnectAccount}
										</Button>
									</Box>
								</Box>
							</Grid.Item>

						</Grid>
					</Box>
				</CardTemplate>
			)}
			<CardTemplate
				cardTitle={tabStrings.generalPreferences}
				id='general-preferences'
			>
				<Grid noBleeds>
					<Grid.Item>
						<Grid noBleeds>
							<Grid.Item>
								<Box>
									{Object.keys(languages).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='language'
											label={STRINGS.preferredLanguage}
											fields={[
												{
													id: 'language_preference',
													label: STRINGS.preferredLanguage,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.language,
													defaultValue: settings?.language_preference,
													options: languages.all_languages,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
							<Grid.Item>
								<Box>
									{Object.keys(timezones).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='timezone'
											label={STRINGS.timezone}
											fields={[
												{
													id: 'timezone',
													label: STRINGS.timezone,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.timezone,
													defaultValue: settings?.timezone,
													options: timezones,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
						</Grid>
					</Grid.Item>
				</Grid>
			</CardTemplate>
		</>
	);
};

MyProfileTab.propTypes = {
	settings: PropTypes.object.isRequired,
	languages: PropTypes.object.isRequired,
	timezones: PropTypes.array.isRequired,
	usesSso: PropTypes.bool.isRequired,
	usesGoogleSSO: PropTypes.bool.isRequired,
	setSettings: PropTypes.func.isRequired,
	reset: PropTypes.bool.isRequired,
	editingFields: PropTypes.object.isRequired,
	setEditingFields: PropTypes.func.isRequired,
	cancelEmailChangeRequest: PropTypes.func.isRequired,
	resendEmailChangeRequest: PropTypes.func.isRequired,
	handleDisconnectGoogleSignOn: PropTypes.func.isRequired,
	assumedRole: PropTypes.object,
};

MyProfileTab.defaultProps = { assumedRole: {} };

export default MyProfileTab;
